import React, { memo, useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import { SENTIMENT } from "./data";

// Lightweight count-up that only animates while `active`.
const useCountUp = (target, active, duration = 1400) => {
  const [value, setValue] = useState(0);
  const raf = useRef(null);

  useEffect(() => {
    if (!active) {
      setValue(0);
      return undefined;
    }
    let start = null;
    const step = (ts) => {
      if (start === null) start = ts;
      const progress = Math.min((ts - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.round(target * eased));
      if (progress < 1) raf.current = requestAnimationFrame(step);
    };
    raf.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf.current);
  }, [target, active, duration]);

  return value;
};

const PIE_COLORS = ["#22c55e", "#f59e0b", "#ef4444"];

const EMOJIS = [
  { e: "😊", delay: 0, x: "8%", y: "12%" },
  { e: "😐", delay: 0.5, x: "78%", y: "8%" },
  { e: "😡", delay: 1, x: "85%", y: "70%" },
];

const AnalyticsDashboard = ({ active }) => {
  const positive = useCountUp(SENTIMENT.positiveCount, active);
  const neutral = useCountUp(SENTIMENT.neutralCount, active);
  const negative = useCountUp(SENTIMENT.negativeCount, active);
  const score = useCountUp(SENTIMENT.sentimentScore, active, 1600);

  const total = SENTIMENT.positiveCount + SENTIMENT.neutralCount + SENTIMENT.negativeCount;
  const pieData = [
    { name: "Positive", value: SENTIMENT.positiveCount },
    { name: "Neutral", value: SENTIMENT.neutralCount },
    { name: "Negative", value: SENTIMENT.negativeCount },
  ];

  const bars = [
    { label: "Positive", value: SENTIMENT.positiveCount, color: "bg-green-500", count: positive },
    { label: "Neutral", value: SENTIMENT.neutralCount, color: "bg-amber-500", count: neutral },
    { label: "Negative", value: SENTIMENT.negativeCount, color: "bg-red-500", count: negative },
  ];

  return (
    <div className="relative h-full w-full">
      {EMOJIS.map((item) => (
        <motion.span
          key={item.e}
          className="absolute z-20 hidden select-none text-2xl sm:block"
          style={{ left: item.x, top: item.y }}
          animate={{ y: [0, -10, 0], rotate: [0, 6, 0] }}
          transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay: item.delay }}
        >
          {item.e}
        </motion.span>
      ))}

      <div className="mx-auto flex h-full w-full max-w-md flex-col overflow-hidden rounded-2xl border border-white/10 bg-[#0b0b0f]/90 p-3.5 shadow-2xl backdrop-blur sm:p-5">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2 sm:mb-4">
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-wider text-white/40 sm:text-xs">Sentiment Overview</p>
            <p className="truncate text-xs font-semibold text-white sm:text-sm">
              {total.toLocaleString()} comments analyzed
            </p>
          </div>
          <span className="shrink-0 rounded-full border border-green-500/30 bg-green-500/10 px-2 py-1 text-[10px] font-semibold text-green-400 sm:px-2.5 sm:text-[11px]">
            {SENTIMENT.engagementQuality} engagement
          </span>
        </div>

        <div className="flex min-h-0 flex-1 flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
          {/* Donut */}
          <div className="relative mx-auto h-28 w-28 flex-shrink-0 sm:mx-0 sm:h-32 sm:w-32">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={pieData}
                  dataKey="value"
                  innerRadius={38}
                  outerRadius={52}
                  paddingAngle={3}
                  stroke="none"
                  isAnimationActive={active}
                  animationDuration={1200}
                >
                  {pieData.map((entry, i) => (
                    <Cell key={entry.name} fill={PIE_COLORS[i]} />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
            <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-xl font-bold text-white sm:text-2xl">{score}</span>
              <span className="text-[10px] text-white/40">score</span>
            </div>
          </div>

          {/* Bars */}
          <div className="min-w-0 flex-1 space-y-2.5 sm:space-y-3">
            {bars.map((b) => {
              const pct = Math.round((b.value / total) * 100);
              return (
                <div key={b.label}>
                  <div className="mb-1 flex items-center justify-between text-[11px]">
                    <span className="text-white/60">{b.label}</span>
                    <span className="font-semibold text-white">{b.count.toLocaleString()}</span>
                  </div>
                  <div className="h-2 overflow-hidden rounded-full bg-white/10">
                    <motion.div
                      className={`h-full rounded-full ${b.color}`}
                      initial={{ width: 0 }}
                      animate={{ width: active ? `${pct}%` : 0 }}
                      transition={{ duration: 1.2, ease: "easeOut" }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Themes */}
        <div className="mt-3 border-t border-white/10 pt-3 sm:mt-5 sm:pt-4">
          <p className="mb-2 text-[10px] uppercase tracking-wider text-white/40 sm:text-[11px]">Top themes</p>
          <div className="flex flex-wrap gap-1.5 sm:gap-2">
            {SENTIMENT.topThemes.map((t, i) => (
              <motion.span
                key={t}
                initial={{ opacity: 0, y: 8 }}
                animate={active ? { opacity: 1, y: 0 } : { opacity: 0, y: 8 }}
                transition={{ delay: 0.4 + i * 0.15 }}
                className="rounded-full border border-white/10 bg-white/5 px-2 py-1 text-[10px] text-white/70 sm:px-2.5 sm:text-[11px]"
              >
                {t}
              </motion.span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default memo(AnalyticsDashboard);
